import React, { useState, useEffect } from "react";
import Modal from "../components/Modal";
import {
  getEvents, createEvent, updateEvent, deleteEvent,
  getTickets,
  getEventStaff, addEventStaff, removeEventStaff,
  getUsers,
  getEventWithdrawals, updateEventWithdrawal,
} from "../../firebase/firestoreService";
import { MOBILE_OPERATORS } from "../../services/paychangu";
import { TICKET_SERVICE_FEE_RATE } from "../../services/ticketing";

const fmt     = (n)  => `MK ${Number(n || 0).toLocaleString()}`;
const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const newTicketTypeId = () => `tt_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

const emptyForm = () => ({
  name: "", description: "", image: "", venue: "", address: "", eventDate: "", ownerId: "",
  ticketTypes: [{ id: newTicketTypeId(), name: "General Admission", price: "", capacity: "" }],
});

// ── Events tab ────────────────────────────────────────────────────────────────
function EventsTab() {
  const [events,     setEvents]     = useState([]);
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(emptyForm());
  const [saving,     setSaving]     = useState(false);
  const [copied,     setCopied]     = useState(null);

  const copyLink = (eventId) => {
    const url = `${window.location.origin}/events/${eventId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(eventId);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const load = async () => {
    setLoading(true);
    const [e, u] = await Promise.all([getEvents(), getUsers()]);
    setEvents(e);
    setUsers(u);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const userLabel = (u) =>
    [u.firstName, u.lastName].filter(Boolean).join(" ") || u.phone || u.email || u.id;

  const openAdd = () => { setEditing(null); setForm(emptyForm()); setModalOpen(true); };
  const openEdit = (event) => {
    setEditing(event);
    const d = event.eventDate?.toDate ? event.eventDate.toDate() : new Date(event.eventDate);
    const localIso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setForm({
      name: event.name || "", description: event.description || "", image: event.image || "",
      venue: event.venue || "", address: event.address || "", eventDate: localIso,
      ownerId: event.ownerId || "",
      ticketTypes: event.ticketTypes?.length ? event.ticketTypes : emptyForm().ticketTypes,
    });
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditing(null); };

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const setTicketType = (i, key, val) =>
    setForm((f) => {
      const tt = [...f.ticketTypes];
      tt[i] = { ...tt[i], [key]: val };
      return { ...f, ticketTypes: tt };
    });

  const addTicketTypeRow    = () => setForm((f) => ({ ...f, ticketTypes: [...f.ticketTypes, { id: newTicketTypeId(), name: "", price: "", capacity: "" }] }));
  const removeTicketTypeRow = (i) => setForm((f) => ({ ...f, ticketTypes: f.ticketTypes.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.name.trim())    { alert("Enter an event name.");   return; }
    if (!form.eventDate)      { alert("Set the event date.");    return; }
    if (!form.ticketTypes.length) { alert("Add at least one ticket type."); return; }
    for (const tt of form.ticketTypes) {
      if (!tt.name.trim() || !tt.price || !tt.capacity) {
        alert("Every ticket type needs a name, price and capacity.");
        return;
      }
    }

    setSaving(true);

    const ticketTypes = form.ticketTypes.map((tt) => ({
      id: tt.id, name: tt.name.trim(), price: Number(tt.price), capacity: Number(tt.capacity),
      sold: tt.sold || 0,
    }));

    const data = {
      name: form.name.trim(), description: form.description.trim(),
      image: form.image.trim(), venue: form.venue.trim(), address: form.address.trim(),
      eventDate: new Date(form.eventDate),
      ownerId: form.ownerId || null,
      ticketTypes,
      ...(editing ? {} : { status: "active" }),
    };

    if (editing) {
      await updateEvent(editing.id, data);
    } else {
      await createEvent(data);
    }

    setSaving(false);
    closeModal();
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;
    await deleteEvent(id);
    load();
  };

  const toggleStatus = async (event) => {
    await updateEvent(event.id, { status: event.status === "active" ? "cancelled" : "active" });
    load();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <button className="btn-primary" onClick={openAdd}>+ Add Event</button>
      </div>

      {loading ? (
        <p className="dash-empty">Loading events…</p>
      ) : events.length === 0 ? (
        <p className="dash-empty">No events yet. Add your first one.</p>
      ) : (
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Date</th>
                <th>Venue</th>
                <th>Owner</th>
                <th>Sold</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => {
                const totalSold     = (e.ticketTypes || []).reduce((s, t) => s + (t.sold || 0), 0);
                const totalCapacity = (e.ticketTypes || []).reduce((s, t) => s + (t.capacity || 0), 0);
                const owner = users.find((u) => u.id === e.ownerId);
                return (
                  <tr key={e.id}>
                    <td><strong>{e.name}</strong></td>
                    <td>{fmtDate(e.eventDate)}</td>
                    <td>{e.venue || <span style={{ color: "#ccc" }}>—</span>}</td>
                    <td>{owner ? userLabel(owner) : <span style={{ color: "#ccc" }}>—</span>}</td>
                    <td style={{ fontWeight: 600 }}>{totalSold} / {totalCapacity}</td>
                    <td>
                      <span style={{
                        background: e.status === "active" ? "#e8f5e9" : "#fde8e8",
                        color:      e.status === "active" ? "#2e7d32" : "#c0392b",
                        fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "3px 8px", borderRadius: 20,
                      }}>
                        {e.status === "active" ? "Active" : "Cancelled"}
                      </span>
                    </td>
                    <td>
                      <button className="btn-edit" onClick={() => copyLink(e.id)}>
                        {copied === e.id ? "Copied!" : "Copy Link"}
                      </button>
                      <button className="btn-edit" onClick={() => openEdit(e)}>Edit</button>
                      <button className="btn-edit" onClick={() => toggleStatus(e)}>
                        {e.status === "active" ? "Cancel" : "Reactivate"}
                      </button>
                      <button className="btn-danger" onClick={() => handleDelete(e.id)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <Modal title={editing ? "Edit Event" : "Add Event"} onClose={closeModal}>
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name}
              onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Good Vibes Live" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date &amp; Time *</label>
              <input className="form-input" type="datetime-local" value={form.eventDate}
                onChange={(e) => setField("eventDate", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Venue</label>
              <input className="form-input" value={form.venue}
                onChange={(e) => setField("venue", e.target.value)} placeholder="e.g. Comesa Hall" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 10, color: "#ccc" }}>(optional)</span></label>
            <input className="form-input" value={form.address}
              onChange={(e) => setField("address", e.target.value)} placeholder="e.g. City Centre, Lilongwe" />
          </div>

          <div className="form-group">
            <label className="form-label">
              Event Owner{" "}
              <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 10, color: "#ccc" }}>
                (optional — lets them see ticket sales and request payout in their profile)
              </span>
            </label>
            <select className="form-select" value={form.ownerId} onChange={(e) => setField("ownerId", e.target.value)}>
              <option value="">— No owner / house event —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{userLabel(u)}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Cover Image URL <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 10, color: "#ccc" }}>(optional)</span></label>
            <input className="form-input" value={form.image}
              onChange={(e) => setField("image", e.target.value)} placeholder="https://…" />
          </div>

          <div className="form-group">
            <label className="form-label">Description <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 10, color: "#ccc" }}>(optional)</span></label>
            <textarea className="form-textarea" rows={3} value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="Short event description…" />
          </div>

          <div className="form-group">
            <label className="form-label">Ticket Types *</label>
            {form.ticketTypes.map((tt, i) => (
              <div key={tt.id} className="ticket-type-entry">
                <input className="form-input" placeholder="Name (e.g. VIP)"
                  value={tt.name} onChange={(e) => setTicketType(i, "name", e.target.value)} />
                <input className="form-input" type="number" placeholder="Price (MK)"
                  value={tt.price} onChange={(e) => setTicketType(i, "price", e.target.value)} />
                <input className="form-input" type="number" placeholder="Capacity"
                  value={tt.capacity} onChange={(e) => setTicketType(i, "capacity", e.target.value)} />
                <button className="btn-remove-row" onClick={() => removeTicketTypeRow(i)}
                  disabled={form.ticketTypes.length === 1}>×</button>
              </div>
            ))}
            <button className="btn-add-row" onClick={addTicketTypeRow}>+ Add ticket type</button>
          </div>

          <div className="form-actions">
            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Event"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Ticket Sales tab ─────────────────────────────────────────────────────────
const TICKET_STATUS_STYLE = {
  Paid:      { background: "#e8f5e9", color: "#2e7d32" },
  Used:      { background: "#e3f2fd", color: "#1565c0" },
  Cancelled: { background: "#fde8e8", color: "#c0392b" },
};

function TicketSalesTab() {
  const [tickets,     setTickets]     = useState([]);
  const [events,      setEvents]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [eventFilter, setEventFilter] = useState("");

  useEffect(() => {
    Promise.all([getTickets(), getEvents()]).then(([t, e]) => {
      setTickets(t);
      setEvents(e);
      setLoading(false);
    });
  }, []);

  const filtered = eventFilter ? tickets.filter((t) => t.eventId === eventFilter) : tickets;
  const totalRevenue = filtered.filter((t) => t.status !== "Cancelled").reduce((s, t) => s + (t.price || 0), 0);
  const platformProfit = totalRevenue * TICKET_SERVICE_FEE_RATE;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <select className="form-input" style={{ maxWidth: 260 }} value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}>
          <option value="">All events</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>

      <div className="stat-cards" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-card__label">Tickets</div>
          <div className="stat-card__value">{filtered.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Ticket Revenue</div>
          <div className="stat-card__value" style={{ fontSize: 20 }}>{fmt(totalRevenue)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Your Profit ({TICKET_SERVICE_FEE_RATE * 100}%)</div>
          <div className="stat-card__value" style={{ fontSize: 20, color: "#2e7d32" }}>{fmt(platformProfit)}</div>
        </div>
      </div>

      <div className="dash-table-wrap">
        <table className="dash-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Event</th>
              <th>Type</th>
              <th>Buyer</th>
              <th>Price</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: "center", color: "#aaa", padding: 32, fontSize: 13 }}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="dash-empty">No tickets sold yet.</td></tr>
            ) : filtered.map((t) => (
              <tr key={t.id}>
                <td>{fmtDate(t.createdAt)}</td>
                <td>{t.eventName}</td>
                <td>{t.ticketTypeName}</td>
                <td>{t.buyerName || "—"}</td>
                <td style={{ fontWeight: 600 }}>{fmt(t.price)}</td>
                <td>
                  <span style={{
                    ...(TICKET_STATUS_STYLE[t.status] || TICKET_STATUS_STYLE.Paid),
                    fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "3px 8px", borderRadius: 20,
                  }}>
                    {t.status || "Paid"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Owner Payouts tab ────────────────────────────────────────────────────────
const WITHDRAWAL_STATUS_STYLE = {
  Pending:  { background: "#fff3e0", color: "#f57c00" },
  Paid:     { background: "#e8f5e9", color: "#2e7d32" },
  Rejected: { background: "#fde8e8", color: "#c0392b" },
};

function OwnerPayoutsTab() {
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [acting,   setActing]   = useState(null);

  const load = async () => {
    setLoading(true);
    setRequests(await getEventWithdrawals());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setActing(id);
    await updateEventWithdrawal(id, { status });
    await load();
    setActing(null);
  };

  const pending = requests.filter((r) => r.status === "Pending");

  return (
    <div>
      <p style={{ fontSize: 12, color: "#aaa", marginBottom: 20, lineHeight: 1.6 }}>
        Event owners request payouts of their ticket revenue here; you send the money manually via
        their chosen mobile money number, then mark the request Paid.
      </p>

      <div className="stat-cards" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-card__label">Pending Requests</div>
          <div className="stat-card__value">{pending.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Pending Amount</div>
          <div className="stat-card__value" style={{ fontSize: 20 }}>
            {fmt(pending.reduce((s, r) => s + (r.amount || 0), 0))}
          </div>
        </div>
      </div>

      <div className="dash-table-wrap">
        <table className="dash-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Owner</th>
              <th>Operator</th>
              <th>Number</th>
              <th>Amount</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "#aaa", padding: 32, fontSize: 13 }}>
                  Loading…
                </td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={7} className="dash-empty">No owner payout requests yet.</td>
              </tr>
            ) : requests.map((r) => (
              <tr key={r.id}>
                <td>{fmtDate(r.createdAt)}</td>
                <td>{r.ownerName || "—"}</td>
                <td>{MOBILE_OPERATORS.find((o) => o.id === r.operator)?.label || r.operator}</td>
                <td>{r.phone}</td>
                <td style={{ fontWeight: 600 }}>{fmt(r.amount)}</td>
                <td>
                  <span style={{
                    ...(WITHDRAWAL_STATUS_STYLE[r.status] || WITHDRAWAL_STATUS_STYLE.Pending),
                    fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "3px 8px", borderRadius: 20,
                  }}>
                    {r.status || "Pending"}
                  </span>
                </td>
                <td>
                  {r.status === "Pending" && (
                    <>
                      <button
                        className="btn-edit"
                        onClick={() => setStatus(r.id, "Paid")}
                        disabled={acting === r.id}
                      >
                        {acting === r.id ? "…" : "Mark Paid"}
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => setStatus(r.id, "Rejected")}
                        disabled={acting === r.id}
                      >
                        Reject
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Staff tab ─────────────────────────────────────────────────────────────────
function StaffTab() {
  const [staff,   setStaff]   = useState([]);
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [adding,  setAdding]  = useState(null);

  const load = async () => {
    setLoading(true);
    const [s, u] = await Promise.all([getEventStaff(), getUsers()]);
    setStaff(s);
    setUsers(u);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const staffIds = new Set(staff.map((s) => s.userId));

  const userName = (u) =>
    [u.firstName, u.lastName].filter(Boolean).join(" ") || u.phone || u.email || u.id;

  const filtered = users.filter((u) => {
    if (staffIds.has(u.id)) return false;
    if (!search.trim()) return false;
    const q = search.toLowerCase();
    return (
      userName(u).toLowerCase().includes(q) ||
      (u.phone || "").includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  });

  const handleAdd = async (user) => {
    setAdding(user.id);
    await addEventStaff({ userId: user.id, name: userName(user), phone: user.phone || "" });
    setSearch("");
    await load();
    setAdding(null);
  };

  const handleRemove = async (id) => {
    await removeEventStaff(id);
    await load();
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <p style={{ fontSize: 12, color: "#aaa", marginBottom: 20, lineHeight: 1.6 }}>
        Assigned staff can log in and scan tickets at the door via the Scan page, without full admin access.
      </p>

      {loading ? (
        <p style={{ fontSize: 13, color: "#aaa" }}>Loading…</p>
      ) : staff.length === 0 ? (
        <p style={{ fontSize: 13, color: "#ccc", marginBottom: 16 }}>No event staff assigned yet.</p>
      ) : (
        <div style={{ marginBottom: 24 }}>
          {staff.map((s) => (
            <div key={s.id} className="admins__row">
              <div>
                <span className="admins__email">{s.name || s.userId}</span>
                {s.phone && <span style={{ fontSize: 11, color: "#aaa", marginLeft: 8 }}>{s.phone}</span>}
              </div>
              <button className="btn-danger" onClick={() => handleRemove(s.id)}>Remove</button>
            </div>
          ))}
        </div>
      )}

      <div className="admins__add-row">
        <input
          className="form-input"
          placeholder="Search by name or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {search.trim() && (
        <div style={{ marginTop: 8, border: "1px solid #e0d9d0", borderRadius: 10, overflow: "hidden" }}>
          {filtered.length === 0 ? (
            <p style={{ fontSize: 12, color: "#aaa", padding: "10px 14px" }}>No matching users found.</p>
          ) : (
            filtered.slice(0, 6).map((u) => (
              <div key={u.id} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px", borderBottom: "1px solid #f5f2ee",
              }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{userName(u)}</span>
                  {u.phone && <span style={{ fontSize: 11, color: "#aaa", marginLeft: 8 }}>{u.phone}</span>}
                </div>
                <button
                  className="btn-edit"
                  onClick={() => handleAdd(u)}
                  disabled={adding === u.id}
                >
                  {adding === u.id ? "Adding…" : "Assign"}
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
export default function EventsSection() {
  const [tab, setTab] = useState("events");

  const TABS = [
    { key: "events",  label: "Events"        },
    { key: "sales",   label: "Ticket Sales"  },
    { key: "payouts", label: "Owner Payouts" },
    { key: "staff",   label: "Staff"         },
  ];

  return (
    <div>
      <div className="dash-main__header">
        <h2 className="dash-main__title">Events</h2>
        <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: "5px 16px", fontSize: 11, fontWeight: 600,
                fontFamily: "inherit", borderRadius: 20, border: "1px solid", cursor: "pointer",
                transition: "all .15s",
                borderColor: tab === t.key ? "#1a1a1a" : "#ddd",
                background:  tab === t.key ? "#1a1a1a" : "#fff",
                color:       tab === t.key ? "#fff"    : "#888",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "events" ? <EventsTab />
        : tab === "sales" ? <TicketSalesTab />
        : tab === "payouts" ? <OwnerPayoutsTab />
        : <StaffTab />}
    </div>
  );
}
